import React, { useEffect, useState } from 'react';
import Pagination from "../components/Pagination.jsx";
import {
  User,
  Calendar,
  Clock,
  RefreshCw,
  AlertCircle,
  Video,
  Copy,
  CheckCircle,
  Loader2,
  UserCheck,
  Play,
  Search,
  ExternalLink
} from 'lucide-react';
import {
  AdminGetBookedOneOnOneSessions,
  GetAllTrainer
} from '../components/Api.jsx';

const statusConfig = {
  BOOKED: { label: 'Booked', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  CONFIRMED: { label: 'Confirmed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Play },
  COMPLETED: { label: 'Completed', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: CheckCircle },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
};

export default function BookedSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [expandedTrainer, setExpandedTrainer] = useState(null);

  const fetchSessions = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await AdminGetBookedOneOnOneSessions();
      setSessions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setSessions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await GetAllTrainer();
      if (Array.isArray(response.data)) {
        setTrainers(response.data);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        setTrainers(response.data.data);
      } else if (response.data?.content && Array.isArray(response.data.content)) {
        setTrainers(response.data.content);
      }
    } catch (error) {
      setTrainers([]);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchTrainers();
  }, []);

  const filteredSessions = sessions.filter(s => {
    if (selectedTrainerId && String(s.trainerId) !== selectedTrainerId) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const userName = (s.userName || '').toLowerCase();
      const userEmail = (s.userEmail || '').toLowerCase();
      const topic = (s.sessionTopic || '').toLowerCase();
      const notes = (s.notes || '').toLowerCase();
      if (!userName.includes(term) && !userEmail.includes(term) && !topic.includes(term) && !notes.includes(term)) return false;
    }
    return true;
  });

  const groupedByTrainer = filteredSessions.reduce((acc, s) => {
    const key = s.trainerId;
    if (!acc[key]) acc[key] = { trainerId: key, trainerName: s.trainerName || `Trainer #${key}`, sessions: [] };
    acc[key].sessions.push(s);
    return acc;
  }, {});

  const trainerCounts = Object.values(groupedByTrainer).map(g => ({
    trainerId: g.trainerId,
    trainerName: g.trainerName,
    count: g.sessions.length,
  }));

  const stats = {
    total: filteredSessions.length,
    booked: filteredSessions.filter(s => s.status === 'BOOKED').length,
    confirmed: filteredSessions.filter(s => s.status === 'CONFIRMED').length,
    inProgress: filteredSessions.filter(s => s.status === 'IN_PROGRESS').length,
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' | ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Booked One-on-One Sessions</h1>
        <button
          onClick={() => fetchSessions(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-50 rounded-lg">
                  <UserCheck className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total Booked</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.booked}</p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.confirmed}</p>
                  <p className="text-sm text-gray-500">Confirmed</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-purple-50 rounded-lg">
                  <Play className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
                  <p className="text-sm text-gray-500">In Progress</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user name, email, or topic..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
            <select
              value={selectedTrainerId}
              onChange={e => setSelectedTrainerId(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white min-w-[200px]"
            >
              <option value="">All Trainers</option>
              {trainers.map(t => (
                <option key={t.id || t.trainerId} value={t.id || t.trainerId}>
                  {t.name || t.trainerName || `Trainer #${t.id || t.trainerId}`}
                </option>
              ))}
            </select>
          </div>

          {filteredSessions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <UserCheck className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No booked one-on-one sessions found</p>
              <p className="text-gray-400 text-sm mt-1">Sessions will appear here once users book them.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedByTrainer).map(([trainerId, group]) => (
                <TrainerGroup
                  key={trainerId}
                  group={group}
                  expandedTrainer={expandedTrainer}
                  setExpandedTrainer={setExpandedTrainer}
                  copiedId={copiedId}
                  copyToClipboard={copyToClipboard}
                  formatDateTime={formatDateTime}
                  formatTime={formatTime}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TrainerGroup({ group, expandedTrainer, setExpandedTrainer, copiedId, copyToClipboard, formatDateTime, formatTime }) {
  const isExpanded = expandedTrainer === group.trainerId;
  const [page, setPage] = useState(1);
  const perPage = 4;
  const totalPages = Math.ceil(group.sessions.length / perPage);
  const paged = group.sessions.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    if (!isExpanded) setPage(1);
  }, [isExpanded]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setExpandedTrainer(isExpanded ? null : group.trainerId)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <User className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">{group.trainerName}</h3>
            <p className="text-sm text-gray-500">{group.sessions.length} session{group.sessions.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          <div className="divide-y divide-gray-50">
            {paged.map(session => {
              const StatusIcon = statusConfig[session.status]?.icon || Clock;
              const statusClass = statusConfig[session.status]?.color || 'bg-gray-100 text-gray-800 border-gray-200';

              return (
                <div key={session.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusClass}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[session.status]?.label || session.status}
                        </span>
                        {session.sessionTopic && (
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {session.sessionTopic}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{session.userName || session.userEmail || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span>{session.slotDate || formatDateTime(session.startTime)?.split('|')[0]?.trim()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                        </div>
                      </div>

                      {session.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">"{session.notes}"</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 lg:flex-shrink-0">
                      {session.meetingLink ? (
                        <>
                          <button
                            onClick={() => copyToClipboard(session.meetingLink, `copy-${session.id}`)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                            title="Copy meeting link"
                          >
                            {copiedId === `copy-${session.id}` ? (
                              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                            {copiedId === `copy-${session.id}` ? 'Copied!' : 'Copy Link'}
                          </button>
                          <a
                            href={session.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Join
                          </a>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No meeting link</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {group.sessions.length > perPage && (
            <div className="p-3 border-t border-gray-100 flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
